import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ShoppingCart, Home, Car, MapPin, Building2, FileText, ArrowRight, Users, BookOpen, Cloud } from 'lucide-react';
import MagneticButton from '../components/MagneticButton';

const webTypes = [
  {
    icon: ShoppingCart,
    title: 'E-shopy',
    desc: 'Vaši zákazníci hledají produkty pomocí AI. Po optimalizaci budou vaše produkty doporučovány přímo v AI odpovědích.',
    color: '#0066FF',
  },
  {
    icon: Home,
    title: 'Reality',
    desc: 'Kupující se ptají AI na lokality, ceny, srovnání bytů. Buďte vidět, když lidé hledají bydlení.',
    color: '#00D4AA',
  },
  {
    icon: Car,
    title: 'Prodej aut / bazary',
    desc: 'Zájemci o auto se ptají AI na srovnání modelů, spotřebu, spolehlivost. Dostaňte nabídku před správné zájemce.',
    color: '#39FF14',
  },
  {
    icon: MapPin,
    title: 'Lokální služby',
    desc: 'Lidé v okolí hledají služby pomocí AI. Buďte vidět ve vašem okolí jako doporučený poskytovatel.',
    color: '#00A8E8',
  },
  {
    icon: Building2,
    title: 'B2B služby',
    desc: 'Rozhodovatelé ve firmách používají AI pro research. Buďte vidět, když hledají řešení, které nabízíte.',
    color: '#0066FF',
  },
  {
    icon: BookOpen,
    title: 'Blogy a publikační weby',
    desc: 'Vaše články mohou být citovány v AI odpovědích. Získejte více čtenářů jako autoritativní zdroj pro AI.',
    color: '#00D4AA',
  },
  {
    icon: Cloud,
    title: 'SaaS služby',
    desc: 'Uživatelé hledají software pomocí AI. Buďte vidět, když se ptají na řešení ve vaší kategorii.',
    color: '#0066FF',
  },
  {
    icon: FileText,
    title: 'Obsahové weby',
    desc: 'Váš obsah může být zdrojem pro AI odpovědi. Získejte více návštěvníků jako referenční zdroj.',
    color: '#39FF14',
  },
];

const WebTypes = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06,
        delayChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <section
      id="webtypes"
      ref={sectionRef}
      className="py-24 px-4 sm:px-6 lg:px-8 relative"
    >
      {/* Background Glow */}
      <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-[#0066FF]/8 rounded-full blur-[150px] -translate-y-1/2 pointer-events-none" />
      <div className="absolute top-1/3 right-0 w-[350px] h-[350px] bg-[#00D4AA]/8 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border-[#00D4AA]/30 mb-6"
            whileHover={{ scale: 1.03 }}
          >
            <Users className="w-4 h-4 text-[#00D4AA]" />
            <span className="text-sm text-slate-600 font-medium">Pro koho</span>
          </motion.div>

          <h2 className="text-headline font-bold mb-6 text-slate-900">
            Pro jaké weby je{' '}
            <span className="text-gradient">optimalizace vhodná</span>
          </h2>

          <p className="text-body text-lg max-w-2xl mx-auto">
            AI optimalizace dává smysl pro širokou škálu webů.
            Podívejte se, jestli jste mezi nimi.
          </p>
        </motion.div>

        {/* Cards Grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {webTypes.map((type, index) => {
            const Icon = type.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{
                  y: -8,
                  transition: { type: 'spring', stiffness: 400, damping: 20 },
                }}
                className="group relative"
              >
                <div className="glass-card rounded-2xl p-6 h-full transition-all duration-500">
                  {/* Glow Effect */}
                  <div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"
                    style={{ background: `radial-gradient(circle at center, ${type.color}20, transparent 70%)` }}
                  />

                  {/* Icon */}
                  <motion.div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-all duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${type.color}15, ${type.color}05)`,
                      border: `1px solid ${type.color}30`,
                      boxShadow: `0 0 15px ${type.color}15`,
                    }}
                    whileHover={{ rotate: 5, scale: 1.08 }}
                  >
                    <Icon className="w-7 h-7" style={{ color: type.color }} />
                  </motion.div>

                  {/* Content */}
                  <h3 className="text-title text-slate-800 mb-3 group-hover:text-gradient transition-colors duration-300">
                    {type.title}
                  </h3>

                  <p className="text-body text-sm">
                    {type.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Link to="/audit">
            <MagneticButton variant="primary">
              Zjistit v bezplatném auditu
              <ArrowRight className="w-5 h-5" />
            </MagneticButton>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default WebTypes;
